<template>
  <!--
    Klarna uses web components to display on-site messaging, so it is declared
    simply with a custom <klarna-placement> tag.
    Read more: https://docs.klarna.com/on-site-messaging/

    /!\ It MUST be registered in `nuxt.config.ts` as a custom element, otherwise
    Vue will try to resolve it. The same must be done in `vitest.config.ts` file
    as well.

    export default defineNuxtConfig({
      vue: {
        compilerOptions: {
          isCustomElement: (tag) => ['klarna-placement'].includes(tag),
        },
      }
    })

    Read more: https://vuejs.org/guide/extras/web-components
  -->
  <!-- eslint-disable-next-line vue/component-name-in-template-casing -->
  <klarna-placement
    ref="klarnaPlacement"
    class="hidden"
    :data-key="placementKey"
    :data-locale="locale"
    :data-purchase-amount="purchaseAmount"
  >
  </klarna-placement>
</template>

<script setup lang="ts">
import { type Ref, computed, ref } from 'vue'

import type { Price, paymentAPI } from '@backmarket/http-api'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useMutationObserver } from '@backmarket/utils/composables/useMutationObserver'
import { noop } from '@backmarket/utils/function/noop'
import { until } from '@backmarket/utils/function/until'
import { priceToMinorUnits } from '@backmarket/utils/math/priceToMinorUnits'
import { toRfc4646Locale } from '@backmarket/utils/string/toRfc4646Locale'

import { KLARNA_LIBRARY_RENDERING_TIMEOUT } from './KlarnaModal.config'
import { useKlarnaLibrary } from './useKlarnaLibrary'

const props = defineProps<{
  paymentMethod: paymentAPI.PaymentMethod
  placementKey: string
  basePrice: Price
}>()

const { market } = useMarketplace()
const locale = computed(() => toRfc4646Locale(market.defaultLocale))

const purchaseAmount = computed(() => priceToMinorUnits(props.basePrice))

const klarnaPlacement: Ref<HTMLElement | null> = ref(null)
const klarnaPlacementButton: Ref<HTMLButtonElement | null> = ref(null)

// Since Klarna library does not provide any hook or callback we can use to
// know when the "placement" element is loaded, we must observe mutations on
// the element, then update klarnaPlacementButton.
useMutationObserver(
  klarnaPlacement,
  () => {
    klarnaPlacementButton.value =
      klarnaPlacement.value
        ?.querySelector('*')
        ?.shadowRoot?.querySelector('button') ?? null
  },
  { childList: true },
)

const { load: loadLibrary } = useKlarnaLibrary(
  props.paymentMethod,
  noop,
  // Lazy-load the library, to improve performance (especially important on the product page).
  { immediate: false },
)

defineExpose({
  /**
   * Load the Klarna library (if not done yet), then wait for the Klarna
   * placement button to be ready.
   *
   * @param timeout Delay in milliseconds to wait for the button to be displayed. 0 for never timed out
   *
   * @returns A Promise that resolves to the placement button.
   * @throws If the library fails to load
   * @throws If the button is not available after `timeout`
   */
  async load(timeout = KLARNA_LIBRARY_RENDERING_TIMEOUT) {
    await loadLibrary()
    await until(klarnaPlacementButton).toBeTruthy({
      timeout,
      throwOnTimeout: true,
    })

    return klarnaPlacementButton.value as HTMLButtonElement
  },

  debug() {
    return klarnaPlacement.value?.outerHTML
  },
})
</script>
